import { api } from "#/lib/api-client";
import type {
  CalendarEvent,
  CalendarEventRequest,
  CalendarImportResult,
  PageResponse,
  ReminderPreference,
} from "./types";

const BASE = "/api/calendar";

export const calendarApi = {
  // ── Everyone ──────────────────────────────────────────────────────────────
  upcoming(): Promise<CalendarEvent[]> {
    return api.get<CalendarEvent[]>(`${BASE}/events/upcoming`).then((r) => r.data);
  },

  downloadIcs(): Promise<Blob> {
    return api.get<Blob>(`${BASE}/events/ics`, { responseType: "blob" }).then((r) => r.data as Blob);
  },

  getReminderPreference(): Promise<ReminderPreference> {
    return api.get<ReminderPreference>(`${BASE}/reminder-preference`).then((r) => r.data);
  },

  setReminderPreference(reminderMinutesBefore: number): Promise<ReminderPreference> {
    return api.put<ReminderPreference>(`${BASE}/reminder-preference`, { reminderMinutesBefore }).then((r) => r.data);
  },

  // ── Admin (Organisation > Calendar) ──────────────────────────────────────
  list(page = 0, size = 20, year?: number | null): Promise<PageResponse<CalendarEvent>> {
    return api
      .get<PageResponse<CalendarEvent>>(`${BASE}/events`, { params: { page, size, year: year ?? undefined } })
      .then((r) => r.data);
  },

  create(payload: CalendarEventRequest): Promise<CalendarEvent> {
    return api.post<CalendarEvent>(`${BASE}/events`, payload).then((r) => r.data);
  },

  update(id: number, payload: CalendarEventRequest): Promise<CalendarEvent> {
    return api.put<CalendarEvent>(`${BASE}/events/${id}`, payload).then((r) => r.data);
  },

  cancel(id: number): Promise<CalendarEvent> {
    return api.post<CalendarEvent>(`${BASE}/events/${id}/cancel`).then((r) => r.data);
  },

  // ── Bulk import (Organisation > Calendar) ────────────────────────────────
  downloadImportTemplate(): Promise<Blob> {
    return api.get<Blob>(`${BASE}/events/import-template`, { responseType: "blob" }).then((r) => r.data as Blob);
  },

  importExcel(file: File): Promise<CalendarImportResult> {
    const form = new FormData();
    form.append("file", file);
    return api
      .post<CalendarImportResult>(`${BASE}/events/import/excel`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  importIcs(file: File): Promise<CalendarImportResult> {
    const form = new FormData();
    form.append("file", file);
    return api
      .post<CalendarImportResult>(`${BASE}/events/import/ics`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
};
